import { isDefined } from "@clipboard-health/util-ts";
import { type SxProps, type Theme } from "@mui/material";

import { MESSAGE_CUSTOM_TYPES, parseChannelUrl } from "../constants";
import type { AdminChatMessage } from "../types";
import { WorkerCredentialProvisionedMessage } from "./WorkerCredentialProvisionedMessage";
import { WorkerCredentialRequestMessage } from "./WorkerCredentialRequestMessage";

interface WorkerCredentialMessageRouterProps {
  message: AdminChatMessage;
  workerName?: string;
  sx?: SxProps<Theme>;
}

/**
 * Routes worker credential admin messages to the appropriate component.
 * Handles both credential request and credential provision completed messages.
 * Extracts workplaceId and workerId from the channel URL.
 * Returns null if required data is missing.
 */
export function WorkerCredentialMessageRouter(
  props: WorkerCredentialMessageRouterProps
): React.ReactElement | null {
  const { message, workerName, sx } = props;

  const channelInfo = parseChannelUrl(message.channelUrl);

  // Need channel info to get workplaceId and workerId for the modal
  if (!isDefined(channelInfo)) {
    return null;
  }

  const { workplaceId, workerId } = channelInfo;
  const resolvedWorkerName = workerName ?? "Worker";

  if (message.customType === MESSAGE_CUSTOM_TYPES.WORKER_CREDENTIAL_PROVISION_REQUEST_COMPLETED) {
    return (
      <WorkerCredentialProvisionedMessage
        data={message.data}
        messageId={message.messageId}
        sx={sx}
        workerId={workerId}
        workerName={resolvedWorkerName}
        workplaceId={workplaceId}
      />
    );
  }

  if (message.customType === MESSAGE_CUSTOM_TYPES.WORKER_CREDENTIAL_REQUEST) {
    return (
      <WorkerCredentialRequestMessage
        message={message.message}
        sx={sx}
        workerId={workerId}
        workerName={resolvedWorkerName}
        workplaceId={workplaceId}
      />
    );
  }

  return null;
}
